import { Style } from '@capacitor/status-bar';
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Button, Col, Form, Modal } from 'react-bootstrap';
import CookieConsent, { getCookieConsentValue } from 'react-cookie-consent';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getFID } from 'web-vitals';
import { useFirebase } from '../contexts/FirebaseContext';
import { useGaming } from '../contexts/GamingContext';
import { performance } from '../firebase';
import { useCapacitor } from '../hooks/useCapacitor';
import { FALLBACK_LANGUAGE_2, useCurrentLanguage } from '../hooks/useCurrentLanguage';
import { useMobileDetect } from '../hooks/useMobileDetect';
import { AdminRoute } from './components/AdminRoute/AdminRoute';
import { BadgeUnlockPopup } from './components/BadgeUnlockPopup/BadgeUnlockPopup';
import { BottomBarNavigation } from './components/BottomBarNavigation/BottomBarNavigation';
import { CapacitorActionsHandler } from './components/CapacitorActionsHandler/CapacitorActionsHandler';
import { Chatbot } from './components/Chatbot/Chatbot';
import { ConditionalWrapper } from './components/ConditionalWrapper/ConditionalWrapper';
import { FlowRoute } from './components/FlowRoute/FlowRoute';
import { FullscreenLoader } from './components/FullscreenLoader/FullscreenLoader';
import { HistoryEvents } from './components/HistoryEvents/HistoryEvents';
import { TrackingEvents } from './components/HubSpotEvents/HubSpotEvents';
import { Legalese } from './components/Legalese/Legalese';
import { Logo } from './components/Logo/Logo';
import { PrivateRoute } from './components/PrivateRoute/PrivateRoute';
import { ScrollToTop } from './components/ScrollToTop/ScrollToTop';
import { TitleHandler } from './components/TitleHandler/TitleHandler';
import { TwoFA } from './pages/2FA/2FA';
import AuthHandler from './pages/AuthHandler/AuthHandler';
import Browse from './pages/Browse/Browse';
import BuyEcm from './pages/BuyEcm/BuyEcm';
import Cache from './pages/Cache/Cache';
import Categorie from './pages/Categorie/Categorie';
import CategorieDetail from './pages/CategorieDetail/CategorieDetail';
import Collaborate from './pages/Collaborate/Collaborate';
import ComingSoon from './pages/ComingSoon/ComingSoon';
import Companies from './pages/Companies/Companies';
import Company from './pages/Company/Company';
import Contacts from './pages/Contacts/Contacts';
import Content from './pages/Content/Content';
import Course from './pages/Course/Course';
import Courses from './pages/Courses/Courses';
import Feedbacks from './pages/Feedbacks/Feedbacks';
import FirstLogin from './pages/FirstLogin/FirstLogin';
import { FlowSicuramente } from './pages/Flows/Sicuramente';
import Gift from './pages/Gift/Gift';
import GiftDetails from './pages/GiftDetails/GiftDetails';
import Help from './pages/Help/Help';
import Home from './pages/Home/Home';
import Lab from './pages/Lab/Lab';
import Legal from './pages/Legal/Legal';
import Logs from './pages/Logs/Logs';
import MyList from './pages/MyList/MyList';
import Plans from './pages/Plans/Plans';
import { Profile } from './pages/Profile/Profile';
import Publish from './pages/Publish/Publish';
import Realtime from './pages/Realtime/Realtime';
import Replace from './pages/Replace/Replace';
import Search from './pages/Search/Search';
import Series from './pages/Series/Series';
import Shop from './pages/Shop/Shop';
import Signup from './pages/Signup/Signup';
import Stats from './pages/Stats/Stats';
import StripeDashboard from './pages/StripeDashboard/StripeDashboard';
import Success from './pages/Success/Success';
import Test from './pages/Test/Test';
import Ticket from './pages/Ticket/Ticket';
import AdminTickets from './pages/AdminTickets/AdminTickets';
import Tootors from './pages/Tootors/Tootors';
import TootorDetail from './pages/TootorDetail/TootorDetail';
import TV from './pages/TV/TV';
import Upload from './pages/Upload/Upload';
import UserNotifications from './pages/UserNotifications/UserNotifications';
import { Users } from './pages/Users/Users';
import VideoReports from './pages/VideoReports/VideoReports';
import { Wait } from './pages/Wait/Wait';
import Watch from './pages/Watch/Watch';

const fidTrace = performance.trace('fid');
fidTrace.start();
getFID(() => fidTrace.stop());

// Sentry.init({
//   enabled: false,
//   dsn: process.env.REACT_APP_SENTRY_URL,
//   environment: process.env.NODE_ENV,
//   integrations: [
//     new Integrations.BrowserTracing({
//       tracingOrigins: ['::1'],
//     }),
//   ],
//   tracesSampleRate: 1.0,
//   release: window['SENTRY_RELEASE']?.id ?? process.env.REACT_APP_VERSION,
//   beforeSend(event: Sentry.Event) {
//     if (window.navigator.userAgent.includes('iubenda-radar')) return null;
//     return event;
//   },
// });

// const withSuspense = (WrappedComponent) => () =>
//   (
//     <Suspense fallback={<FullscreenLoader />}>
//       <WrappedComponent />
//     </Suspense>
//   );

export default function App() {
  const { isTrial, isLoggedIn, currentUser, sendEmailVerification, logout, database, staticData, userDataFmt, fv } = useFirebase();
  const {
    track,
    openedNotification,
    setOpenedNotification,
    showDataModal,
    setShowDataModal,
    formLoading,
    setFormLoading,
    formData,
    setFormData,
    setPauseVideo,
  } = useGaming();

  const [configLoaded, setConfigLoaded] = useState(true);
  const [isVerifyEmailModalOpen, setIsVerifyEmailModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isUpdateAvailableModalOpen, setIsUpdateAvailableModalOpen] = useState(false);
  const [suggestApp, setSuggestApp] = useState(false);
  const [inVideo, setInVideo] = useState(false);

  const { isNative, platform, StatusBar, Orientation } = useCapacitor();
  const { isMobile, isTV } = useMobileDetect();
  const { i18n, t } = useTranslation();
  const { currentLanguage, updateLanguage } = useCurrentLanguage();

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    const notificationId = openedNotification.id ?? openedNotification.data.id;
    database
      .doc(`userData/${currentUser.uid}/notifications/${notificationId}`)
      .set({ data: { ...openedNotification.data, form: formData, redeemed: true, redeemedAt: fv.serverTimestamp() } }, { merge: true })
      .then(() => {
        track('DATA_SUBMITTED', { id: notificationId });
      })
      .catch(console.error)
      .finally(() => {
        setFormLoading(false);
        setShowDataModal(false);
        setOpenedNotification(null);
        setPauseVideo(false);
      });
  };

  const handleChange = (event) => {
    setFormData({
      name: event.target.name,
      value: event.target.value,
    });
  };

  const handleRefresh = () => {
    window.location.reload();
    return Promise.resolve();
  };

  useEffect(() => {
    (async () => {
      if (userDataFmt?.profile?.formData?.categoriaProfessionale === 'aso') {
        await i18n.changeLanguage(FALLBACK_LANGUAGE_2);
        await updateLanguage(FALLBACK_LANGUAGE_2, false);
      } else {
        await i18n.changeLanguage(currentLanguage);
      }
    })();

    const cleanup = database.doc('system/actions').onSnapshot((snapshot) => {
      const data = snapshot.data();
      for (const [key, value] of Object.entries(data)) {
        switch (key) {
          case 'FORCE_RELOAD': {
            const storedValue = localStorage.getItem('__FR_VERSION');
            if (value != storedValue) {
              localStorage.setItem('__FR_VERSION', value);
              window.location.reload();
            }
            break;
          }
          case 'ADMIN_UPDATE': {
            const storedValue = localStorage.getItem('__AU_VERSION');
            if (value != storedValue) {
              localStorage.setItem('__AU_VERSION', value);
              if (!!userDataFmt?.admin) setIsUpdateAvailableModalOpen(true);
            }
            break;
          }
        }
      }
    });

    return () => cleanup();
  }, [userDataFmt]);

  const resetHubspotChatBottom = useCallback(() => {
    document.documentElement.style.setProperty('--hubspot-chat-bottom', '20px');
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('lang', currentLanguage);
  }, []);

  useEffect(() => {
    if (getCookieConsentValue()) {
      resetHubspotChatBottom();
    }
  }, []);

  useEffect(() => {
    if (isNative) {
      if (isTV) Orientation?.lock('landscape').catch(console.error);
      else Orientation?.lock('portrait').catch(console.error);
      if (platform == 'ios') {
        StatusBar.setStyle({ style: Style.Dark }).catch(console.error);
      }
    }
  }, [isNative, isTV]);

  useEffect(() => {
    setSuggestApp(isMobile && !isNative && isLoggedIn);
  }, [isMobile, isNative, isLoggedIn]);

  useEffect(() => {
    if (!currentUser || !staticData) return;
    if (window.location.pathname.startsWith('/__/auth/action')) return;
    if (staticData.FORCE_EMAIL_VERIFICATION) setIsVerifyEmailModalOpen(!currentUser.emailVerified);
  }, [currentUser, staticData]);

  const verifyEmail = () => {
    sendEmailVerification('/browse').then(() => {
      setIsVerifyEmailModalOpen(false);
      setIsConfirmModalOpen(true);
    });
  };

  const handleLogout = async () => {
    await logout();
    window.location.reload();
  };

  if (!configLoaded || !Object.keys(staticData).length) return <FullscreenLoader />;

  if (isVerifyEmailModalOpen)
    return (
      <Modal show={true} backdrop={'static'} keyboard={false} centered>
        <div style={{ position: 'absolute', top: '-42px', display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Logo style={{ height: '36px' }} />
        </div>
        <Modal.Header>
          <Modal.Title>{t('app.labels.verificaNecessaria')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('app.labels.verificaEmail')}
          <br />
          {t('app.labels.verificaEmail2')}
        </Modal.Body>
        <Modal.Footer>
          <Button className="tootor-button tootor-button--basic" onClick={() => handleLogout()}>
            {t('labels.logout')}
          </Button>
          <Button className="tootor-button tootor-button--secondary ml-auto" onClick={() => verifyEmail()}>
            {t('app.labels.inviaMailConferma')}
          </Button>
        </Modal.Footer>
      </Modal>
    );

  if (isConfirmModalOpen)
    return (
      <Modal show={true} backdrop={'static'} keyboard={false} centered>
        <div style={{ position: 'absolute', top: '-42px', display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Logo style={{ height: '36px' }} />
        </div>
        <Modal.Header>
          <Modal.Title>{t('app.labels.emailInviata')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('app.labels.verificaMailEClicca')}</Modal.Body>
        <Modal.Footer>
          <Button className="tootor-button tootor-button--basic" onClick={() => handleLogout()}>
            {t('labels.logout')}
          </Button>
          <Button className="tootor-button tootor-button--secondary ml-auto" onClick={() => verifyEmail()}>
            {t('app.labels.inviaMailConferma2')}
          </Button>
        </Modal.Footer>
      </Modal>
    );

  return (
    <>
      <ScrollToTop />
      <TitleHandler />
      <CapacitorActionsHandler />
      <TrackingEvents />
      <HistoryEvents enabled={false} />

      <ConditionalWrapper
        condition={isNative}
        wrapper={(children) => (
          <PullToRefresh
              className={(() => {
                let classes = [];
                if (isNative) classes.push('scrollable-viewport');
                if (!isLoggedIn || (!isTrial && inVideo)) classes.push('force-full-height');
                return classes.join(' ');
              })()}
            onRefresh={handleRefresh}
            isPullable={false}
          >
            {children}
          </PullToRefresh>
        )}
      >
        <Switch>
          <Route path={'/'} exact component={Home} />
          <Route path={'/signup'} component={Signup} />
          <Route path={'/help'} component={Help} />
          <Route path={'/legal/:doc?'} component={Legal} />
          <Route path={'/company-info'} component={Company} />
          <Route path={'/shop'} component={Shop} />
          <Route path={'/__/auth/action'} component={AuthHandler} />
          <Route path={'/collaborate'} component={Collaborate} />
          <Route path={'/contact-us'} component={Contacts} />
          <PrivateRoute path={'/browse'} component={Browse} />
          <PrivateRoute exact path={'/categorie'} component={Categorie} />
          <PrivateRoute path={'/categorie/:id'} component={CategorieDetail} />
          <PrivateRoute path={'/wait'} component={Wait} />
          <PrivateRoute path={'/list'} component={MyList} />
          <PrivateRoute path={'/search'} component={Search} />
          <PrivateRoute path={'/first-login'} component={FirstLogin} />
          <Route path={'/watch/:id'} component={Watch} />
          <PrivateRoute path={'/series/:id'} component={Series} />
          <PrivateRoute path={'/profile'} component={Profile} />
          <PrivateRoute path={'/courses'} component={Courses} /*countryRestrictions={['IT']}*/ />
          <PrivateRoute path={'/company/:id/courses'} component={Courses} /*countryRestrictions={['IT']}*/ />
          <PrivateRoute path={'/course/:courseId/watch/:id'} component={Watch} /*countryRestrictions={['IT']}*/ />
          <PrivateRoute path={'/course/:courseId/test/:id'} component={Test} /*countryRestrictions={['IT']}*/ />
          <PrivateRoute path={'/course/:id'} component={Course} /*countryRestrictions={['IT']}*/ />
          <PrivateRoute path={'/plans'} component={Plans} />
          <PrivateRoute path={'/success'} component={Success} />
          <PrivateRoute path={'/tv'} component={TV} />
          <PrivateRoute path={'/tootors/:idTootor'} component={TootorDetail} />
          <PrivateRoute exact path={'/tootors'} component={Tootors} />
          <PrivateRoute path={'/ticket/:id'} component={Ticket} />
          <PrivateRoute exact path={'/gift'} component={Gift} /*countryRestrictions={['IT']}*/ />
          <PrivateRoute path={'/gift/details/:id'} component={GiftDetails} /*countryRestrictions={['IT']}*/ />
          <PrivateRoute path={'/notifications'} component={UserNotifications}></PrivateRoute>
          {/* For local development only, comment the prop "countryRestrictions={['IT]}" */}
          <PrivateRoute path={'/buy_ecm'} component={BuyEcm} /*countryRestrictions={['IT']}*/ />
          <FlowRoute path={'/flow/sicuramente'} flow={'sicuramente'} component={FlowSicuramente} />
          <AdminRoute path={'/admin/tickets'} component={AdminTickets} />
          <AdminRoute path={'/admin/ticket/:id'} component={Ticket} />
          <AdminRoute path={'/admin/reports'} component={VideoReports} />
          <AdminRoute path={'/admin/feedbacks'} component={Feedbacks} />
          <AdminRoute path={'/admin/realtime'} component={Realtime} />
          <AdminRoute path={'/admin/users'}>
            <Users />
          </AdminRoute>
          <AdminRoute path={'/admin/publish'} component={Publish} />
          <AdminRoute path={'/admin/replace'} component={Replace} />
          <AdminRoute path={'/admin/coming-soon'} component={ComingSoon} />
          <AdminRoute path={'/admin/content'} component={Content} />
          <AdminRoute path={'/admin/stats'} component={Stats} />
          <AdminRoute path={'/admin/logs'} component={Logs} />
          <AdminRoute path={'/admin/lab'} component={Lab} />
          <AdminRoute path={'/admin/cache'} component={Cache} />
          <AdminRoute path={'/admin/upload'} component={Upload} />
          <AdminRoute path={'/admin/stripe-dashboard'} component={StripeDashboard} />
          <AdminRoute path={'/admin/companies'} component={Companies} />
          <AdminRoute path={'/admin/2fa'} component={TwoFA} />
          <Route path={'*'} component={() => <Redirect to={'/'} />} />
        </Switch>
      </ConditionalWrapper>

      <BottomBarNavigation onSetInVideo={setInVideo} />
      {isLoggedIn && <Chatbot />}

      {!isNative && (
        <CookieConsent
          onAccept={resetHubspotChatBottom}
          buttonText={'Ok'}
          disableButtonStyles={true}
          location={'bottom'}
          containerClasses={'align-items-center'}
          buttonClasses={'tootor-button tootor-button--basic m-2'}
          contentClasses={'m-2'}
        >
          <div>🍪&nbsp; {t('app.labels.sitoUsaCookie')}</div>
          <small>
            {t('app.labels.controllaDati')} <Legalese just={'privacy'} /> {t('app.labels.controllaDati2')} <Legalese just={'cookie'} />.
          </small>
        </CookieConsent>
      )}

      <Modal show={isUpdateAvailableModalOpen} onHide={() => setIsUpdateAvailableModalOpen(false)} centered>
        <Modal.Header>
          <Modal.Title>È disponibile un aggiornamento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>Non è obbligatorio effettuarlo subito, ma è necessario per disporre delle nuove funzionalità e va quindi eseguito quanto prima.</div>
        </Modal.Body>
        <Modal.Footer>
          <Button className="tootor-button tootor-button--basic" onClick={() => setIsUpdateAvailableModalOpen(false)}>
            Più tardi
          </Button>
          <Button className="tootor-button tootor-button--secondary" onClick={() => window.location.reload()}>
            Aggiorna
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />

      {showDataModal && (
        <Modal show={showDataModal} onHide={() => setShowDataModal(false)} centered scrollable>
          <Modal.Header>
            <Modal.Title as={'h5'}>{t('labels.redeemReward')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {openedNotification.data?.['payload.text'] && <p>{openedNotification.data['payload.text']}</p>}
            <Form onSubmit={handleFormSubmit}>
              <Form.Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      name={'nome'}
                      disabled={formLoading}
                      value={formData.nome}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.nome')}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Control
                      name={'cognome'}
                      disabled={formLoading}
                      value={formData.cognome}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.cognome')}
                      required
                    />
                  </Form.Group>
                </Col>
              </Form.Row>

              <Form.Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      name={'indirizzo'}
                      disabled={formLoading}
                      value={formData.indirizzo}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.riga1')}
                      required
                    />
                  </Form.Group>
                </Col>
              </Form.Row>

              <Form.Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      name={'indirizzo2'}
                      disabled={formLoading}
                      value={formData.indirizzo2}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.riga2')}
                    />
                  </Form.Group>
                </Col>
              </Form.Row>

              <Form.Row>
                <Col xs={3}>
                  <Form.Group>
                    <Form.Control
                      name={'cap'}
                      disabled={formLoading}
                      value={formData.cap}
                      onChange={handleChange}
                      placeholder={'CAP*'}
                      type={'number'}
                      min={0}
                      max={99999}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Control
                      name={'citta'}
                      disabled={formLoading}
                      value={formData.citta}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.citta')}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col xs={3}>
                  <Form.Group>
                    <Form.Control
                      name={'provincia'}
                      disabled={formLoading}
                      value={formData.provincia}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.provincia')}
                      minLength={2}
                      maxLength={2}
                      required
                    />
                  </Form.Group>
                </Col>
              </Form.Row>

              <Form.Row>
                <Col>
                  <Form.Group>
                    <Form.Control
                      name={'email'}
                      disabled={formLoading}
                      value={formData.email}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.email')}
                      type={'email'}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Control
                      name={'telefono'}
                      disabled={formLoading}
                      value={formData.telefono}
                      onChange={handleChange}
                      placeholder={t('adventCalendar.labels.telefono')}
                      type={'tel'}
                      required
                    />
                  </Form.Group>
                </Col>
              </Form.Row>

              <Form.Row>
                <Col>
                  <Form.Check
                    type={'checkbox'}
                    disabled={formLoading}
                    name={'checkProfilazione'}
                    onChange={(e) =>
                      setFormData({
                        name: e.target.name,
                        value: e.target.checked,
                      })
                    }
                    checked={formData.checkProfilazione}
                    label={
                      <div className={'small text-muted'} style={{ lineHeight: '1rem' }}>
                        {t('adventCalendar.labels.accettoProfilazione', { title: process.env.REACT_APP_TITLE })}{' '}
                      </div>
                    }
                  />
                  <Form.Check
                    type={'checkbox'}
                    disabled={formLoading}
                    name={'checkComunicazioniDaTootor'}
                    onChange={(e) =>
                      setFormData({
                        name: e.target.name,
                        value: e.target.checked,
                      })
                    }
                    checked={formData.checkComunicazioniDaTootor}
                    label={
                      <div className={'small text-muted'} style={{ lineHeight: '1rem' }}>
                        {t('adventCalendar.labels.accettoComunicazioniDaTootor', { title: process.env.REACT_APP_TITLE })}{' '}
                      </div>
                    }
                  />
                  <Form.Check
                    type={'checkbox'}
                    disabled={formLoading}
                    name={'checkMarketingSocietaTerze'}
                    onChange={(e) =>
                      setFormData({
                        name: e.target.name,
                        value: e.target.checked,
                      })
                    }
                    checked={formData.checkMarketingSocietaTerze}
                    label={
                      <div className={'small text-muted'} style={{ lineHeight: '1rem' }}>
                        {t('adventCalendar.labels.accettoMarketingSocietaTerze', { title: process.env.REACT_APP_TITLE })}{' '}
                      </div>
                    }
                  />
                  {openedNotification.data['customPrivacyPolicyLink'] && (
                    <Form.Check
                      type={'checkbox'}
                      disabled={formLoading}
                      name={'customPrivacyPolicyLink'}
                      onChange={(e) =>
                        setFormData({
                          name: e.target.name,
                          value: e.target.checked,
                        })
                      }
                      checked={formData.customPrivacyPolicyLink}
                      label={
                        <span className={'small text-muted'}>
                          <a href={openedNotification.data['customPrivacyPolicyLink']}>{openedNotification.data['customPrivacyPolicyLink']}</a>
                        </span>
                      }
                    />
                  )}
                </Col>
              </Form.Row>

              <div className={'mt-3 d-flex justify-content-end'}>
                <Button
                  variant={'secondary'}
                  type={'submit'}
                  disabled={formLoading}
                  className={'mr-2'}
                  onClick={() => {
                    setPauseVideo(false);
                    setShowDataModal(false);
                  }}
                >
                  {t('labels.nonOra')}
                </Button>
                <Button variant={'info'} type={'submit'} disabled={formLoading}>
                  {formLoading ? t('labels.invioInCorso') : t('labels.invia')}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}

      <BadgeUnlockPopup />
    </>
  );
}
